import React, { useEffect, useMemo } from 'react';

import ActuatorDetail from '../../atoms/ActuatorDetail/ActuatorDetail';
import AddDevicePopup from '../../atoms/AddDevicePopup/AddDevicePopup';
import ApplianceDetail from '../../atoms/ApplianceDetail/ApplianceDetail';
import DetectorDetail from '../../atoms/DetectorDetail/DetectorDetail';
import DeviceDetail from '../../atoms/DeviceDetail/DeviceDetail';
import DeviceSelector from '../../atoms/DeviceSelector/DeviceSelector';
import Col from '../../atoms/Layout/Col/Col';
import Row from '../../atoms/Layout/Row/Row';
import NewDevice from '../../atoms/NewDevice/NewDevice';
import NewController from '../../atoms/NewThermostat/NewThermostat';
import TechPopup from '../../atoms/TechPopup/TechPopup';
import ThermostatDetail from '../../atoms/ThermostatDetail/ThermostatDetail';
import Tutorial from '../../atoms/Tutorial/Tutorial';
import { createConfigurationSignature } from '../../config/Functions/CreateConfigurationSignature';
import { getFromLocalStorage } from '../../Functions/LocalStorageUtils';
import { setControllerType, setHasUnsavedChanges, setSavedConfigurationSignature } from '../../redux/reducers/appReducer';
import { useAppSelector } from '../../redux/store';
import { useAppDispatch } from '../../redux/store';
import FloorplanComponent from '../Floorplan/FloorplanComponent';
import BottomBarComponent from '../Navigational/BottomBarComponent';
import LeftMenuComponent from '../Navigational/LeftMenuComponent';
import RightMenuComponent from '../Navigational/RightMenuComponent';
import TopBarComponent from '../Navigational/TopBarComponent';
import SaveComponent from '../Save/SaveComponent';

const Layout: React.FC = () => {
  const dispatch = useAppDispatch();
  let controllerType = useAppSelector((state) => state.app.controllerType);
  const selectingDevice = useAppSelector((state) => state.app.selectingDevice);
  const selectingThermostat = useAppSelector((state) => state.app.selectingThermostat);
  const deviceSelectorCategory = useAppSelector((state) => state.app.deviceSelectorCategory);
  const showDeviceDetail = useAppSelector((state) => state.app.showDeviceDetail);
  const showSaveDetail = useAppSelector((state) => state.app.showSaveDetail);
  const showThermostatDetail = useAppSelector((state) => state.app.showThermostatDetail);
  const showDetectorDetail = useAppSelector((state) => state.app.showDetectorDetail);
  const showAddDevicePopup = useAppSelector((state) => state.app.showAddDevicePopup);
  const showActuatorDetail = useAppSelector((state) => state.app.showActuatorDetail);
  const showApplianceDetail = useAppSelector((state) => state.app.showApplianceDetail);
  const lastSavedConfigurationSignature = useAppSelector((state) => state.app.lastSavedConfigurationSignature);
  const hasUnsavedChanges = useAppSelector((state) => state.app.hasUnsavedChanges);
  const controllers = useAppSelector((state) => state.controllers.devices);
  const appliances = useAppSelector((state) => state.app.appliances);
  const actuators = useAppSelector((state) => state.app.actuators);
  const detectors = useAppSelector((state) => state.app.detectors);
  if(controllerType === '') controllerType = getFromLocalStorage('tech') || 'wireless';
  const disableAddDevicePopup = getFromLocalStorage('hideAddDevicePopup') === 'true' ? true : false;

  const currentConfigurationSignature = useMemo(() => {
    return createConfigurationSignature({
      controllers,
      appliances,
      actuators,
      detectors
    });
  }, [controllers, appliances, actuators, detectors]);
  
  useEffect(() => {
    dispatch(setControllerType(controllerType));
  }, [controllerType, dispatch]);

  useEffect(() => {
    if (lastSavedConfigurationSignature === null) {
      dispatch(setSavedConfigurationSignature(currentConfigurationSignature));
      return;
    }

    const shouldMarkUnsaved = currentConfigurationSignature !== lastSavedConfigurationSignature;
    if (shouldMarkUnsaved !== hasUnsavedChanges) {
      dispatch(setHasUnsavedChanges(shouldMarkUnsaved));
    }
  }, [
    currentConfigurationSignature,
    dispatch,
    hasUnsavedChanges,
    lastSavedConfigurationSignature
  ]);

  return (
    <>
      <Tutorial />
      <Row>
        <Col className="main-col" height="100vh">
          {controllerType === '' && (
            <TechPopup />
          )}
          {selectingDevice && selectingThermostat && (
            <NewController />
          )}
          {selectingDevice && deviceSelectorCategory === 'infront_of_door_unit' && (
            <DeviceSelector type="infront_of_door_unit" />
          )}
          {selectingDevice && !selectingThermostat && deviceSelectorCategory === 'controller' && (
            <NewDevice />
          )}
          {showDeviceDetail && (
            <DeviceDetail />
          )}
          {showSaveDetail && (
            <SaveComponent />
          )}
          {showThermostatDetail && (
            <ThermostatDetail />
          )}
          {showDetectorDetail && (
            <DetectorDetail />
          )}
          {(showAddDevicePopup && !disableAddDevicePopup) && (
            <AddDevicePopup />
          )}
          {showActuatorDetail && (
            <ActuatorDetail />
          )}
          {showApplianceDetail && (
            <ApplianceDetail />
          )}
          <Row height="auto">
            <TopBarComponent />
          </Row>
          <Row style={{ position: 'relative' }}>
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                bottom: 0,
                zIndex: 3
              }}
            >
              <LeftMenuComponent />
            </div>
            <Col>
              <FloorplanComponent />
              <BottomBarComponent />
            </Col>
            <div
              style={{
                position: 'absolute',
                top: 0,
                right: 0,
                bottom: 0,
                zIndex: 3
              }}
            >
              <RightMenuComponent />
            </div>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default Layout;
